import { Deferred, Head, router, usePage } from '@inertiajs/react'
import { BanknotesIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { TransactionStatusBadge } from '@/components/common/TransactionStatusBadge'
import { MonoId } from '@/components/common/MonoId'
import { EmptyState } from '@/components/common/EmptyState'
import { GatewayNotice } from '@/components/common/GatewayNotice'
import { Pagination } from '@/components/common/Pagination'
import { TableSkeleton } from '@/components/common/Skeletons'
import { ExportMenu } from '@/components/common/ExportMenu'
import { TransactionFilters, type FilterValues, type FilterOptions } from '@/components/common/TransactionFilters'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { GatewayState, GatewayError, PageResult, PayoutRecord, SharedProps } from '@/types'

interface PayoutsIndexProps {
  payouts?: { page: PageResult<PayoutRecord> | null; error: GatewayError | null }
  filters: FilterValues
  filter_options: FilterOptions
  per: number
  gateway: GatewayState
}

function PayoutsIndex({ payouts, filters, filter_options, per, gateway }: PayoutsIndexProps) {
  const { t, locale } = useTranslations()
  const { props } = usePage<SharedProps>()
  const canManage = ['superadmin', 'admin'].includes(props.auth.user?.role ?? '')
  const manageHref = gateway.merchant ? `/merchants/${gateway.merchant.id}/edit` : undefined

  function navigate(nextFilters: FilterValues, nextPage = 1) {
    router.get('/payouts', { filters: nextFilters, page: nextPage, per }, { preserveScroll: true, preserveState: true, replace: true })
  }

  return (
    <>
      <Head title={t('payouts.title')} />
      <PageHeading
        title={t('payouts.title')}
        actions={
          gateway.configured && (
            <>
              <Button outline onClick={() => router.reload({ only: ['payouts'], data: { refresh: true } })}>
                {t('common.refresh')}
              </Button>
              <ExportMenu kind="payouts" filters={filters} />
            </>
          )
        }
      />

      <div className="mt-6 flex flex-col gap-6">
        <GatewayNotice state={gateway} manageHref={canManage ? manageHref : undefined} createHref={canManage ? '/merchants' : undefined} />

        {gateway.configured && (
          <>
            <TransactionFilters filters={filters} options={filter_options} onApply={(f) => navigate(f)} />

            <Deferred data="payouts" fallback={<TableSkeleton rows={6} cols={4} />}>
              {payouts?.error ? (
                <GatewayNotice state={{ ...gateway, error: payouts.error }} />
              ) : payouts?.page && payouts.page.items.length > 0 ? (
                <div className="flex flex-col gap-4">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>{t('payments.columns.date')}</TableHeader>
                        <TableHeader>{t('payments.columns.transaction')}</TableHeader>
                        <TableHeader>{t('payments.columns.status')}</TableHeader>
                        <TableHeader className="text-right">{t('payments.columns.amount')}</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payouts.page.items.map((p, i) => (
                        <TableRow key={p.transaction_id ?? i} href={p.transaction_id ? `/payouts/${p.transaction_id}` : undefined}>
                          <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at, locale)}</TableCell>
                          <TableCell>
                            {p.transaction_id ? (
                              <MonoId value={p.transaction_id} />
                            ) : (
                              <span className="text-text/50">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.status ? <TransactionStatusBadge status={p.status} family={p.status_family} /> : '—'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-text-strong">
                            {formatMoney(p.amount, p.currency ?? 'EUR', locale)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination pagination={payouts.page.pagination} onNavigate={(n) => navigate(filters, n)} />
                </div>
              ) : (
                <EmptyState
                  icon={BanknotesIcon}
                  title={t('payouts.empty')}
                  action={
                    <Button outline onClick={() => navigate({})}>
                      {t('payments.filters.clear')}
                    </Button>
                  }
                />
              )}
            </Deferred>
          </>
        )}
      </div>
    </>
  )
}

PayoutsIndex.layout = withAppLayout

export default PayoutsIndex
